/**
 * GPU model resolution for cloud instance shapes.
 *
 * The provider catalogs (public/assets/providers.json) encode the accelerator
 * MODEL in each shape's name/id but carry no explicit field, so the instance
 * dropdown could only ever show a generic "N× GPU". This module resolves the
 * concrete model (T4, A100, L4, H100, ...) per provider so the dropdown reads
 * "1× NVIDIA T4", "4× L4", etc.
 *
 * Resolution order for a given shape:
 *   1. An explicit field on the catalog entry (`gpu_type` / `accelerator_type`),
 *      if one is ever added upstream — this always wins so a hand-set value can
 *      override inference.
 *   2. A provider-specific resolver (family conventions + model tokens baked
 *      into the shape name).
 *   3. A generic model-token scan as a last resort.
 *
 * Everything here is display-only; it does not change what gets submitted.
 */

// Short model code -> friendly full name (tooltip / aria text). Anything not
// listed falls back to "NVIDIA <code>".
export const GPU_FULL_NAMES = {
    K80: 'NVIDIA Tesla K80',
    M60: 'NVIDIA Tesla M60',
    P4: 'NVIDIA Tesla P4',
    P40: 'NVIDIA Tesla P40',
    P100: 'NVIDIA Tesla P100',
    V100: 'NVIDIA Tesla V100',
    T4: 'NVIDIA T4',
    T4G: 'NVIDIA T4G',
    A10: 'NVIDIA A10',
    A10G: 'NVIDIA A10G',
    A100: 'NVIDIA A100',
    'A100-80G': 'NVIDIA A100 80GB',
    L4: 'NVIDIA L4',
    L40S: 'NVIDIA L40S',
    H100: 'NVIDIA H100',
    H200: 'NVIDIA H200',
    MI25: 'AMD Radeon Instinct MI25',
    MI300X: 'AMD Instinct MI300X',
    GAUDI: 'Habana Gaudi',
    TRAINIUM: 'AWS Trainium',
    INFERENTIA: 'AWS Inferentia',
};

// Model tokens that may appear literally inside a shape name/id, ordered so
// longer/more-specific codes match before their prefixes (l40s before l4,
// a10g before a10, p100 before p4). Matched case-insensitively as a substring.
const MODEL_TOKENS = [
    ['mi300x', 'MI300X'],
    ['h200', 'H200'],
    ['h100', 'H100'],
    ['l40s', 'L40S'],
    ['l4', 'L4'],
    ['a100', 'A100'],
    ['a10g', 'A10G'],
    ['a10', 'A10'],
    ['v100', 'V100'],
    ['p100', 'P100'],
    ['p40', 'P40'],
    ['p4', 'P4'],
    ['k80', 'K80'],
    ['t4g', 'T4G'],
    ['t4', 'T4'],
    ['m60', 'M60'],
];

function scanTokens(text) {
    const t = (text || '').toLowerCase();
    for (const [needle, model] of MODEL_TOKENS) {
        if (t.indexOf(needle) !== -1) return model;
    }
    return null;
}

// Normalize an explicit catalog value (e.g. "nvidia-tesla-a100") to a short
// code by stripping the vendor prefix and scanning what remains.
function normalizeExplicit(value) {
    if (!value) return null;
    const cleaned = String(value).toLowerCase().replace(/^nvidia-tesla-/, '').replace(/^nvidia-/, '');
    return scanTokens(cleaned) || cleaned.toUpperCase();
}

// AWS: the accelerator is tied to the instance FAMILY (prefix before the dot),
// not embedded in the name — so it needs an explicit lookup.
const AWS_FAMILY = {
    p2: 'K80', p3: 'V100', p4d: 'A100', p4de: 'A100-80G', p5: 'H100', p5e: 'H200', p5en: 'H200',
    g3: 'M60', g3s: 'M60', g4dn: 'T4', g4ad: 'MI25', g5: 'A10G', g5g: 'T4G', g6: 'L4', gr6: 'L4', g6e: 'L40S',
    dl1: 'GAUDI', trn1: 'TRAINIUM', trn1n: 'TRAINIUM', trn2: 'TRAINIUM', inf1: 'INFERENTIA', inf2: 'INFERENTIA',
};

// GCP: fixed-GPU families carry the model by family; n1 is attach-optional and
// has no fixed model (resolved live per zone in the form).
const GCP_FAMILY = {
    'a2-ultragpu': 'A100-80G', 'a2-highgpu': 'A100', 'a2-megagpu': 'A100',
    'a3-ultragpu': 'H200', 'a3-megagpu': 'H100', 'a3-highgpu': 'H100', 'a3-edgegpu': 'H100',
    'g2-': 'L4',
};

// OTC (Open Telekom Cloud): GPU by family prefix (before the dot).
const OTC_FAMILY = {
    p1: 'P100', p2: 'V100', p2v: 'V100', p2vs: 'V100', p3: 'V100', pi1: 'P4', pi2: 'T4',
    g5: 'A10', g6: 'A10',
};

function resolveAws(id) {
    const family = (id.split('.')[0] || '').toLowerCase();
    return AWS_FAMILY[family] || null;
}

function resolveGcp(id) {
    const lower = id.toLowerCase();
    for (const prefix of Object.keys(GCP_FAMILY)) {
        if (lower.startsWith(prefix)) return GCP_FAMILY[prefix];
    }
    return null;
}

function resolveOtc(id) {
    const family = (id.split('.')[0] || '').toLowerCase();
    return OTC_FAMILY[family] || scanTokens(id);
}

// Oracle / OCI: model lives in the shape name. GPU3=V100, GPU2=P100, GPU4=A100;
// A10/A100/H100/L40S/MI300X are spelled out. Check spelled-out tokens first.
function resolveOracle(id) {
    const lower = id.toLowerCase();
    const token = scanTokens(lower);
    if (token) return token;
    if (/gpu4/.test(lower)) return 'A100';
    if (/gpu3/.test(lower)) return 'V100';
    if (/gpu2/.test(lower)) return 'P100';
    return null;
}

// Azure: most GPU SKUs embed the model between underscores (NC24ads_A100_v4).
// The MI25-based NVv4 and P40-based NDs v1 series carry no token.
function resolveAzure(id) {
    const token = scanTokens(id);
    if (token) return token;
    const lower = id.toLowerCase();
    if (/^standard_nv\d+as_v4/.test(lower)) return 'MI25';      // NVv4 (partial AMD GPU)
    if (/^standard_nd\d+s$/.test(lower)) return 'P40';          // NDs v1
    return null;
}

/**
 * Resolve the accelerator model for a catalog instance.
 * @param {object} instance - catalog entry (has instance_id / name, maybe gpu_type)
 * @param {string} [provider] - 'aws'|'azure'|'gcp'|'oracle'|'ibm'|'otc'|'mass'
 * @returns {string|null} short model code (e.g. 'A100'), or null if unknown
 */
export function getGpuModel(instance, provider) {
    if (!instance) return null;

    // 1. Explicit field always wins.
    const explicit = instance.gpu_type || instance.accelerator_type || instance.acceleratorType;
    if (explicit) return normalizeExplicit(explicit);

    const id = instance.instance_id || (instance.name || '').split(' ')[0] || '';
    const name = instance.name || '';

    // 2. Provider-specific resolvers.
    let model = null;
    switch (provider) {
        case 'aws': model = resolveAws(id); break;
        case 'gcp': model = resolveGcp(id); break;
        case 'azure': model = resolveAzure(id); break;
        case 'oracle': model = resolveOracle(id); break;
        case 'mass': model = resolveOracle(id); break; // OCI-style catalog
        case 'ibm': model = scanTokens(id); break;     // model is a suffix token
        case 'otc': model = resolveOtc(id); break;
        default: break;
    }
    if (model) return model;

    // 3. Generic fallback across id + display name.
    return scanTokens(id) || scanTokens(name);
}

/**
 * Fixed (built-in) GPU for a shape, read from the catalog entry's GPU count.
 * Returns { model, count } for shapes that ship a GPU (a2/g2, p3, NC*, ...), or
 * null for CPU / attach-optional shapes. Used to show a read-only "includes N×
 * MODEL" note for fixed-GPU shapes where the GPU is not user-selectable.
 */
export function getFixedGpu(instance, provider) {
    if (!instance) return null;
    const raw = instance.gpus ?? instance.gpu_count ?? instance.gpuCount ?? instance.accelerator_count ?? instance.gpu;
    const count = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (!(typeof count === 'number' && isFinite(count) && count > 0)) return null;
    return { model: getGpuModel(instance, provider), count };
}

/** Friendly full name for a short model code (for tooltips). */
export function gpuFullName(model) {
    if (!model) return '';
    return GPU_FULL_NAMES[model] || ('NVIDIA ' + model);
}

/**
 * True when a shape supports GPUs but does NOT ship with a fixed one — i.e. an
 * accelerator can optionally be attached. Today only GCP N1 shapes behave this
 * way; the actual attachable models are zone-dependent and resolved live in the
 * cluster form. Used to flag "GPU-optional" shapes in the instance dropdown so
 * users see accelerator support before selecting.
 */
export function isAcceleratorOptional(instanceId, provider) {
    if (!instanceId) return false;
    if (provider === 'gcp') return String(instanceId).toLowerCase().startsWith('n1-');
    return false;
}
